import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import { useTranslation } from "react-i18next";

import { canAccessPath } from "@/lib/routePermissions";
import { consumeReturnPath } from "@/lib/returnPath";
import type { User } from "@/types";

interface Props {
  /** Resolves with the signed-in user, so the landing decision below can check
   *  their permissions before honouring a deep link. */
  onSsoCallback: (code: string, redirectUri: string) => Promise<User | null | void>;
}

/** Which published, account-less resource an SSO round-trip belongs to. Both
 *  reuse this one redirect URI (already registered with the IdP for login), so
 *  neither needs any IdP reconfiguration — `t` is what tells them apart. */
type PublicResource = "portal" | "diagram";

interface PortalState {
  t: PublicResource;
  slug: string;
  nonce: string;
  silent?: boolean;
}

/** API path + in-app return route for each published resource kind. */
const RESOURCE_ROUTES: Record<PublicResource, { api: string; view: (s: string) => string }> = {
  portal: { api: "web-portals", view: (slug) => `/portal/${slug}` },
  diagram: { api: "diagrams", view: (slug) => `/embed/diagram/${slug}` },
};

function parsePortalState(raw: string | null): PortalState | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(atob(raw));
    if (
      parsed &&
      (parsed.t === "portal" || parsed.t === "diagram") &&
      typeof parsed.slug === "string"
    ) {
      return parsed as PortalState;
    }
  } catch {
    // Not a published-resource state — a normal login callback.
  }
  return null;
}

/**
 * Shared OAuth redirect target (/auth/callback). Handles both normal user login
 * and SSO-gated web-portal sign-in — the two are told apart by the OAuth
 * `state`. Reusing one redirect URI means an SSO portal needs no extra IdP
 * registration beyond the login one that already works.
 */
export default function SsoCallback({ onSsoCallback }: Props) {
  const { t } = useTranslation(["auth", "common"]);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState("");
  // Set when a portal sign-in is denied for a real reason (e.g. email domain
  // not allowed) — distinct from the silent-auth fallback, which bounces the
  // visitor back to the portal's sign-in button without an error.
  const [portalDenied, setPortalDenied] = useState<{ slug: string; message: string } | null>(null);

  useEffect(() => {
    const code = searchParams.get("code");
    const errorParam = searchParams.get("error");
    const errorDesc = searchParams.get("error_description");
    const portalState = parsePortalState(searchParams.get("state"));

    if (portalState) {
      handlePortalReturn(portalState, code, errorParam);
      return;
    }

    // ---- normal user login callback ----
    // Single-use CSRF state, stored by LoginPage just before redirecting (#860).
    const storedState = sessionStorage.getItem("sso_login_state");
    sessionStorage.removeItem("sso_login_state");
    // Single-use like the nonce, and read here rather than inside the .then()
    // so the IdP-error, state-mismatch, no-code and token-exchange-failure
    // paths clear it too — a stale value must never hijack a later sign-in.
    const returnPath = consumeReturnPath();
    if (errorParam) {
      setError(errorDesc || errorParam);
      return;
    }
    if (!storedState || storedState !== searchParams.get("state")) {
      setError(t("sso.stateMismatch"));
      return;
    }
    if (!code) {
      setError(t("sso.noCodeGeneric"));
      return;
    }
    const redirectUri = `${window.location.origin}/auth/callback`;
    onSsoCallback(code, redirectUri)
      .then((signedIn) => {
        if (!returnPath) {
          navigate("/", { replace: true });
          return;
        }
        // Fail-closed: no user, or no permissions on it, means the dashboard.
        const perms = (signedIn as User | null | undefined)?.permissions;
        if (canAccessPath(perms, returnPath)) {
          navigate(returnPath, { replace: true });
          return;
        }
        navigate("/", { replace: true, state: { deniedPath: returnPath } });
      })
      .catch((err) => setError(err instanceof Error ? err.message : t("sso.failed")));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function handlePortalReturn(state: PortalState, code: string | null, errorParam: string | null) {
    const { slug, nonce } = state;
    const routes = RESOURCE_ROUTES[state.t];
    const flagKey = `portal_silent_${state.t}_${slug}`;
    const back = () => navigate(routes.view(slug), { replace: true });

    // CSRF: the nonce must match the one stored just before we redirected.
    const storedNonce = sessionStorage.getItem("portal_sso_nonce");
    sessionStorage.removeItem("portal_sso_nonce");
    if (!storedNonce || storedNonce !== nonce) {
      sessionStorage.setItem(flagKey, "failed");
      back();
      return;
    }

    // IdP returned an error (silent attempt needs interaction, or the user
    // cancelled): fall back to the portal's explicit sign-in button.
    if (errorParam || !code) {
      sessionStorage.setItem(flagKey, "failed");
      back();
      return;
    }

    // Exchange the code for an account-less portal-session cookie.
    const redirectUri = `${window.location.origin}/auth/callback`;
    fetch(`/api/v1/${routes.api}/public/${slug}/sso/callback`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code, redirect_uri: redirectUri }),
    })
      .then(async (res) => {
        if (!res.ok) {
          const err = await res.json().catch(() => ({ detail: res.statusText }));
          throw new Error(err.detail || res.statusText);
        }
        sessionStorage.removeItem(flagKey);
        back();
      })
      .catch((e) => {
        // A real denial (domain not allowed / email unverified) — show it
        // rather than looping the visitor back through sign-in.
        sessionStorage.setItem(flagKey, "failed");
        setPortalDenied({
          slug,
          message: e instanceof Error ? e.message : t("common:portal.signInError"),
        });
      });
  }

  if (portalDenied) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#1a1a2e",
          gap: 2,
          px: 3,
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 500 }}>
          {portalDenied.message}
        </Alert>
        <Button
          variant="contained"
          onClick={() => navigate(`/portal/${portalDenied.slug}`, { replace: true })}
          sx={{ mt: 2 }}
        >
          {t("common:actions.retry")}
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "#1a1a2e",
        gap: 2,
      }}
    >
      {error ? (
        <>
          <Alert severity="error" sx={{ maxWidth: 500 }}>
            {error}
          </Alert>
          <Button
            variant="contained"
            onClick={() => navigate("/", { replace: true })}
            sx={{ mt: 2 }}
          >
            {t("sso.backToLogin")}
          </Button>
        </>
      ) : (
        <>
          <CircularProgress sx={{ color: "#64b5f6" }} />
          <Typography color="#fff">{t("sso.completing")}</Typography>
        </>
      )}
    </Box>
  );
}
